/**
 * `/settings/webhooks/:uuid/edit` — edit-endpoint slide-over.
 *
 * Loads the endpoint via `useEndpoint(uuid)`, renders the same form
 * shape as `new.tsx` pre-filled, and submits PATCH with the diff
 * (server accepts partial updates — we send every field the user
 * changed, but sending every field verbatim is also fine and avoids
 * complex diff logic).
 *
 * Extra actions that only apply to an existing row:
 *   - **Signing secret** — masked by default with a "Show" button.
 *     Plaintext is returned by `GET /v1/webhooks/endpoints/{uuid}`
 *     (org admins can look it up for redeploys without rotating).
 *     During the 24h grace window after a rotate, the previous
 *     secret is rendered below with its expiry.
 *   - **Send test event** → `POST /v1/webhooks/endpoints/{uuid}/test`.
 *     Bypasses outbox; inline HTTP call returns `{ delivered,
 *     status_code, duration_ms, error }` which we toast. User-error
 *     cases (unreachable URL, non-2xx response) surface via a
 *     purpose-built `toastError` instead of a generic `FetchError`
 *     so the toast explains which endpoint failed and why.
 *   - **Enable / Disable** → PATCH `{ status }`. A manual
 *     `status: 'enabled'` on an auto-disabled endpoint clears
 *     `consecutive_failures` server-side.
 *   - **Rotate secret** → handled in the list page row action; we
 *     render a link to that action rather than duplicating the
 *     secret-reveal dance inside the slide-over.
 *   - **Delete** → also handled in the list page row action.
 */
import { SecretReveal } from '@/components/primitives/SecretReveal';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetPanel,
  SheetPopup,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { FetchError } from '@/lib/api';
import { toastError, toastFromError, toastSuccess } from '@/lib/errors';
import type { UpdateEndpointBody, WebhookEndpointKind } from '@/lib/queries';
import { useEndpoint, useTestEndpoint, useUpdateEndpoint } from '@/lib/queries';
import { cn } from '@/lib/utils';
import { PaperPlaneTilt, Plus, Trash } from '@phosphor-icons/react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { type FormEvent, type ReactElement, useEffect, useState } from 'react';

export const Route = createFileRoute('/_app/settings/webhooks/$uuid/edit')({
  component: EditWebhookSlideOver,
});

// Mirrors `new.tsx` — keep the two in sync. `monitor.regression.detected`
// is intentionally absent: no producer emits it yet (Phase 5 work).
const EVENT_CATALOGUE: { id: string; label: string; hint: string }[] = [
  {
    id: 'audit.completed',
    label: 'audit.completed',
    hint: 'Fires when a snapshot finishes successfully.',
  },
  {
    id: 'audit.failed',
    label: 'audit.failed',
    hint: 'Fires when a snapshot errors out or times out.',
  },
  {
    id: 'audit.started',
    label: 'audit.started',
    hint: 'Fires when a snapshot starts running. High volume — opt in.',
  },
  {
    id: 'monitor.run.failed',
    label: 'monitor.run.failed',
    hint: 'Fires when a monitor run ends with one or more failed snapshots.',
  },
  {
    id: 'usage.threshold.warning',
    label: 'usage.threshold.warning',
    hint: 'Fires when monthly usage on any plan axis crosses 80%.',
  },
  {
    id: 'usage.threshold.critical',
    label: 'usage.threshold.critical',
    hint: 'Fires when monthly usage on any plan axis crosses 90%.',
  },
];

const KINDS: { value: WebhookEndpointKind; label: string }[] = [
  { value: 'generic', label: 'Generic' },
  { value: 'slack', label: 'Slack' },
  { value: 'discord', label: 'Discord' },
];

const MAX_HEADERS = 8;

interface HeaderRow {
  key: string;
  value: string;
}

function EditWebhookSlideOver(): ReactElement {
  const { uuid } = Route.useParams();
  const navigate = useNavigate();
  const endpoint = useEndpoint(uuid);
  const update = useUpdateEndpoint(uuid);
  const testFire = useTestEndpoint();

  const [open, setOpen] = useState(true);
  const [url, setUrl] = useState('');
  const [kind, setKind] = useState<WebhookEndpointKind>('generic');
  const [description, setDescription] = useState('');
  const [eventTypes, setEventTypes] = useState<string[]>([]);
  const [headers, setHeaders] = useState<HeaderRow[]>([]);
  const [statusEnabled, setStatusEnabled] = useState(true);
  // Per-endpoint HMAC signing toggle. Hydrated from the row; flipping
  // it here PATCHes `signing_enabled`. The signing secret stays where
  // it is regardless — toggling on later activates it without
  // requiring a rotation.
  const [signingEnabled, setSigningEnabled] = useState(false);
  const [formError, setFormError] = useState<string | undefined>(undefined);
  const [hydrated, setHydrated] = useState(false);

  // Hydrate form from the API response — only once so the user's
  // in-progress edits aren't clobbered by a refetch.
  useEffect(() => {
    if (hydrated || !endpoint.data) return;
    const e = endpoint.data;
    setUrl(e.url);
    setKind(e.kind);
    setDescription(e.description ?? '');
    setEventTypes(e.event_types);
    setHeaders(Object.entries(e.headers).map(([key, value]) => ({ key, value })));
    setStatusEnabled(e.status === 'enabled');
    setSigningEnabled(e.signing_enabled);
    setHydrated(true);
  }, [endpoint.data, hydrated]);

  function toggleEvent(id: string): void {
    setEventTypes((prev) => (prev.includes(id) ? prev.filter((e) => e !== id) : [...prev, id]));
  }

  function addHeader(): void {
    if (headers.length >= MAX_HEADERS) return;
    setHeaders((prev) => [...prev, { key: '', value: '' }]);
  }

  function updateHeader(idx: number, patch: Partial<HeaderRow>): void {
    setHeaders((prev) => prev.map((h, i) => (i === idx ? { ...h, ...patch } : h)));
  }

  function removeHeader(idx: number): void {
    setHeaders((prev) => prev.filter((_, i) => i !== idx));
  }

  function onSheetOpenChange(next: boolean): void {
    setOpen(next);
    if (!next) navigate({ to: '/settings/webhooks' });
  }

  async function onTestFire(): Promise<void> {
    try {
      const res = await testFire.mutateAsync({ uuid });
      if (res.delivered) {
        toastSuccess(
          'Test event delivered',
          `Receiver responded ${res.status_code ?? 'OK'} in ${res.duration_ms}ms.`,
        );
        return;
      }

      // Not a server-side bug — the test POST either got a non-2xx
      // response or the TCP connection never completed. Surface a
      // focused error with the receiver URL so the user can see this
      // is their endpoint, not our API.
      const target = endpoint.data?.url ?? 'the endpoint URL';
      if (res.status_code !== null) {
        // Receiver answered, but not with a 2xx. The body excerpt (if
        // any) is the most useful hint.
        const excerpt = res.response_body_excerpt
          ? ` — ${res.response_body_excerpt.slice(0, 120)}`
          : '';
        toastError(
          `Receiver rejected the test (${res.status_code})`,
          `${target} responded ${res.status_code} in ${res.duration_ms}ms${excerpt}`,
        );
      } else {
        // Network-level failure — DNS, TCP, TLS, or Worker-side
        // reachability (Cloudflare Workers can't hit localhost from
        // `wrangler dev` without a tunnel).
        toastError(
          `Couldn't reach the endpoint`,
          `${target} — ${res.error ?? 'network error'}. Check the URL is a public HTTPS endpoint your receiver is listening on.`,
        );
      }
    } catch (err) {
      if (err instanceof FetchError) {
        toastFromError(err);
      } else {
        toastFromError(err);
      }
    }
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setFormError(undefined);

    if (!url.trim()) {
      setFormError('URL is required.');
      return;
    }

    const headersRecord: Record<string, string> = {};
    for (const row of headers) {
      const k = row.key.trim();
      const v = row.value.trim();
      if (!k && !v) continue;
      if (!k) {
        setFormError('Header name is required when a value is set.');
        return;
      }
      headersRecord[k] = v;
    }

    const body: UpdateEndpointBody = {
      url: url.trim(),
      kind,
      description: description.trim() || null,
      event_types: eventTypes,
      headers: headersRecord,
      status: statusEnabled ? 'enabled' : 'disabled',
      signing_enabled: signingEnabled,
    };

    try {
      await update.mutateAsync(body);
      toastSuccess('Endpoint updated', `${url.trim()} saved.`);
      onSheetOpenChange(false);
    } catch (err) {
      if (err instanceof FetchError) setFormError(err.message);
      else toastFromError(err);
    }
  }

  const isLoading = endpoint.isLoading || !hydrated;
  const autoDisabled = endpoint.data?.auto_disabled_at !== null;

  return (
    <Sheet open={open} onOpenChange={onSheetOpenChange}>
      <SheetPopup className="sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>Edit webhook endpoint</SheetTitle>
          <SheetDescription>
            Changes apply to future deliveries. The signing secret is unchanged — rotate it from the
            endpoints list if you need a fresh secret.
          </SheetDescription>
        </SheetHeader>
        {isLoading ? (
          <SheetPanel>
            <div className="space-y-3 p-6">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-32 w-full" />
            </div>
          </SheetPanel>
        ) : (
          <form onSubmit={onSubmit} noValidate className="contents">
            <SheetPanel>
              <div className="space-y-5 p-6">
                {autoDisabled && (
                  <div className="rounded-lg border border-[var(--color-danger)]/30 bg-[var(--color-danger)]/10 p-3 text-sm text-[var(--color-danger)]">
                    This endpoint was auto-disabled after too many consecutive delivery failures (
                    {endpoint.data?.consecutive_failures}). Saving with status{' '}
                    <span className="font-mono">enabled</span> will clear the counter and resume
                    deliveries.
                  </div>
                )}

                <Field>
                  <FieldLabel>Destination</FieldLabel>
                  <KindSegmented value={kind} onChange={setKind} />
                </Field>

                <Field>
                  <FieldLabel htmlFor="webhook-edit-url">URL</FieldLabel>
                  <Input
                    id="webhook-edit-url"
                    type="url"
                    required
                    value={url}
                    onChange={(e) => setUrl((e.target as HTMLInputElement).value)}
                  />
                </Field>

                <Field>
                  <FieldLabel htmlFor="webhook-edit-description">Description</FieldLabel>
                  <Input
                    id="webhook-edit-description"
                    value={description}
                    onChange={(e) => setDescription((e.target as HTMLInputElement).value)}
                  />
                </Field>

                {/* Signing secret + previous-secret blocks only
                    render while the toggle is on. With signing off
                    the secret is still on the row (so flipping the
                    toggle on later doesn't require rotation) but
                    showing it would imply we're sending it on the
                    wire — which we aren't. */}
                {signingEnabled && (
                  <>
                    <SecretReveal
                      label="Signing secret"
                      value={endpoint.data?.signing_secret ?? null}
                      copyToastLabel="Signing secret copied"
                    />

                    {endpoint.data?.signing_secret_previous &&
                      endpoint.data.signing_secret_rotated_at && (
                        <SecretReveal
                          label="Previous secret (rotation grace)"
                          value={endpoint.data.signing_secret_previous}
                          copyToastLabel="Previous secret copied"
                          hint={
                            <>
                              Valid until{' '}
                              <span className="font-mono">
                                {new Date(
                                  new Date(endpoint.data.signing_secret_rotated_at).getTime() +
                                    24 * 60 * 60 * 1000,
                                ).toLocaleString()}
                              </span>
                              . Both secrets verify while this window is open.
                            </>
                          }
                        />
                      )}
                  </>
                )}

                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <Checkbox
                      id="webhook-edit-enabled"
                      checked={statusEnabled}
                      onCheckedChange={(next) => setStatusEnabled(next === true)}
                    />
                    <label
                      htmlFor="webhook-edit-enabled"
                      className="text-sm font-medium text-[var(--color-ink)]"
                    >
                      Enabled — receive events
                    </label>
                  </div>
                  <p className="text-xs text-[var(--color-mute)]">
                    Disable to silence this endpoint without deleting it. Disabled endpoints keep
                    their history and signing secret.
                  </p>
                </div>

                <div className="space-y-2">
                  <div className="text-sm font-medium text-[var(--color-ink)]">Events</div>
                  <p className="text-xs text-[var(--color-mute)]">
                    Leave every box unchecked to receive all events.
                  </p>
                  <ul className="space-y-2">
                    {EVENT_CATALOGUE.map((e) => {
                      const checked = eventTypes.includes(e.id);
                      return (
                        <li
                          key={e.id}
                          className="flex items-start gap-3 rounded-lg border border-[var(--color-line)] p-3"
                        >
                          <Checkbox
                            checked={checked}
                            onCheckedChange={() => toggleEvent(e.id)}
                            aria-label={e.label}
                          />
                          <div className="flex-1">
                            <div className="font-mono text-xs text-[var(--color-ink)]">
                              {e.label}
                            </div>
                            <div className="text-xs text-[var(--color-mute)]">{e.hint}</div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-[var(--color-ink)]">
                      Static headers
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={addHeader}
                      disabled={headers.length >= MAX_HEADERS}
                    >
                      <Plus aria-hidden="true" />
                      Add header
                    </Button>
                  </div>
                  {headers.length > 0 && (
                    <div className="space-y-2">
                      {headers.map((h, idx) => (
                        <div
                          key={`header-${
                            // biome-ignore lint/suspicious/noArrayIndexKey: header rows keyed by position
                            idx
                          }`}
                          className="flex items-center gap-2"
                        >
                          <Input
                            placeholder="X-Authorization"
                            value={h.key}
                            onChange={(e) =>
                              updateHeader(idx, { key: (e.target as HTMLInputElement).value })
                            }
                          />
                          <Input
                            placeholder="Bearer …"
                            value={h.value}
                            onChange={(e) =>
                              updateHeader(idx, { value: (e.target as HTMLInputElement).value })
                            }
                          />
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            aria-label="Remove header"
                            onClick={() => removeHeader(idx)}
                          >
                            <Trash aria-hidden="true" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Per-endpoint HMAC signing toggle. Hydrated from
                    the row; PATCH submits the new value alongside
                    other fields. The signing secret stays where it
                    is regardless of this toggle — flipping it on
                    activates the existing secret without rotation. */}
                <div className="space-y-2 rounded-lg border border-[var(--color-line)] p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="space-y-1">
                      <div className="text-sm font-medium text-[var(--color-ink)]">
                        Sign requests with HMAC-SHA256
                      </div>
                      <p className="text-xs text-[var(--color-mute)]">
                        When on, every delivery includes an{' '}
                        <span className="font-mono">X-Clickweave-Signature</span> header your
                        receiver can verify with the signing secret. Slack and Discord don't verify
                        signatures.
                      </p>
                    </div>
                    <Switch
                      checked={signingEnabled}
                      onCheckedChange={setSigningEnabled}
                      aria-label="Sign requests with HMAC-SHA256"
                    />
                  </div>
                </div>

                {formError && <FieldError>{formError}</FieldError>}
              </div>
            </SheetPanel>
            <SheetFooter className="flex items-center justify-between gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={onTestFire}
                loading={testFire.isPending}
              >
                <PaperPlaneTilt aria-hidden="true" />
                Send test event
              </Button>
              <div className="flex items-center gap-2">
                <Button type="button" variant="ghost" onClick={() => onSheetOpenChange(false)}>
                  Cancel
                </Button>
                <Button type="submit" loading={update.isPending}>
                  Save changes
                </Button>
              </div>
            </SheetFooter>
          </form>
        )}
      </SheetPopup>
    </Sheet>
  );
}

function KindSegmented({
  value,
  onChange,
}: {
  value: WebhookEndpointKind;
  onChange: (next: WebhookEndpointKind) => void;
}): ReactElement {
  return (
    <div
      // biome-ignore lint/a11y/useSemanticElements: segmented control uses div+role="group" to match existing dashboard pattern
      role="group"
      aria-label="Destination kind"
      className="inline-flex items-center overflow-hidden rounded-md border border-[var(--color-line)] bg-[var(--color-paper)] text-sm"
    >
      {KINDS.map((k) => {
        const active = k.value === value;
        return (
          <button
            key={k.value}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(k.value)}
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-1.5 outline-none transition-colors',
              active
                ? 'bg-[var(--color-paper-alt)] font-medium text-[var(--color-ink)]'
                : 'text-[var(--color-ink-soft)] hover:bg-[var(--color-paper-alt)]/60',
            )}
          >
            {k.label}
          </button>
        );
      })}
    </div>
  );
}
